import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  interpolate,
  useAnimate,
  useAnimationFrame,
  useInView
} from 'framer-motion';
import styled from 'styled-components';
import { easing } from 'ts-easing';

import { combineRefs } from '@/utils/react';

type Wave = { head: number; tail: number };

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CanvasContainer = styled.div`
  position: relative;
  flex-grow: 1;
  height: 12px;
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

const Canvas = styled.canvas`
  position: absolute;
  display: block;
  inset: 0;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

const Label = styled.label`
  display: grid;

  > * {
    grid-area: 1 / 1;
  }

  input {
    opacity: 0;
  }

  input + span {
    transition: outline 0.1s ease-out;
    outline: 0px solid ${({ theme }) => theme.borderColor.LOUD};
  }

  input:focus-visible + span {
    outline: 4px solid ${({ theme }) => theme.borderColor.LOUD};
  }

  &&& span {
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.fontColor.DEFAULT};
    border: 1px solid ${({ theme }) => theme.fontColor.DEFAULT};
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    border-radius: 999px;
    padding: 0.25em 1em;
    font-size: 12px;
    line-height: 1;
    font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  }

  &&& span.overlay {
    background-color: ${({ theme }) => theme.fontColor.DEFAULT};
    color: #fff;
  }

  span.overlay {
    clip-path: inset(0% 100% 0% 0% round 10px);
    transition: clip-path var(--duration, 1s) cubic-bezier(0.22, 1, 0.36, 1);
  }

  input:checked ~ span.overlay {
    clip-path: inset(0% 0% 0% 0% round 10px);
  }
`;

function formatTimestamp(t: number) {
  const sec = Math.floor(t) % 60;
  const min = Math.floor(t / 60) % 60;
  const hrs = Math.floor(t / (60 * 60));
  const pad = (v: number) => `${v}`.padStart(2, '0');
  return hrs > 0
    ? `${pad(hrs)}:${pad(min)}:${pad(sec)}`
    : `${pad(min)}:${pad(sec)}`;
}

const defaultWaveOpts = {
  frequency: 20,
  t: 0,
  amplitude: 1,
  waves: [] as Wave[],
  rampEase: easing.inOutSine,
  ramp: 200,
  w: 0,
  h: 0,
  dpr: 1,
  color: '#000'
};
function drawWave(
  canvas: HTMLCanvasElement,
  _opts: Partial<typeof defaultWaveOpts> = {}
) {
  const opts = { ...defaultWaveOpts, ..._opts };

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.clearRect(0, 0, opts.w, opts.h);

  const lineWidth = 1 * opts.dpr;
  ctx.strokeStyle = opts.color;
  ctx.lineCap = 'round';
  ctx.lineWidth = lineWidth;

  ctx.beginPath();
  const start = interpolate([0, 1], [-opts.ramp, opts.w]);
  const end = interpolate([0, 1], [0, opts.w + opts.ramp]);

  const waveProgress = opts.waves.map(w => {
    const head = interpolate([start(w.head), end(w.head)], [1, 0]);
    const tail = interpolate([start(w.tail), end(w.tail)], [0, 1]);
    return (x: number) => head(x) * tail(x);
  });

  for (let x = lineWidth; x <= opts.w - lineWidth; x += 1) {
    let a = waveProgress.reduce((acc, p) => acc + p(x), 0);
    a = opts.rampEase(a);
    a *= opts.amplitude;
    a *= opts.h * 0.5 - lineWidth;
    let y = opts.h * 0.5;
    y += Math.sin(x * (1 / opts.frequency) * Math.PI + opts.t) * a;

    if (x === 0) {
      ctx.moveTo(0, y);
      continue;
    }
    ctx.lineTo(x, y);
  }
  ctx.stroke();
}

export function AudioSnippet({
  src,
  start,
  end
}: {
  end: number;
  src: string;
  start: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const inView = useInView(canvasRef);
  const [fifoWaveQueue] = useState<Map<Wave, Wave>>(
    () => new Map<Wave, Wave>()
  );
  const activeWave = useRef<Wave | null>(null);
  const waveOptsRef = useRef({
    amplitude: 1,
    color: '#000',
    dpr: 1,
    h: 0,
    w: 0
  });
  const [scope, animate] = useAnimate();

  const updateWaveOpts = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const styles = getComputedStyle(canvas);
    const bb = canvas.parentElement?.getBoundingClientRect();
    const height = bb?.height || 0;
    const width = bb?.width || 0;
    const color = styles.getPropertyValue('color');
    const dpr = window.devicePixelRatio;

    const w = width * dpr;
    const h = height * dpr;
    canvas.setAttribute('width', `${w}`);
    canvas.setAttribute('height', `${h}`);

    waveOptsRef.current.h = h;
    waveOptsRef.current.w = w;
    waveOptsRef.current.dpr = dpr;
    waveOptsRef.current.color = color;
  }, []);

  useLayoutEffect(() => {
    updateWaveOpts();

    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(updateWaveOpts);
    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, [updateWaveOpts]);

  const audioOnOffTransition = useMemo(
    () => ({
      duration: 1,
      ease: easing.inOutSine,
      onUpdate(v: number) {
        const audio = audioRef.current;
        if (!audio) return;

        audio.volume = v;
        waveOptsRef.current.amplitude = audio.volume;
      }
    }),
    []
  );
  const audioControls = useMemo(() => {
    let ctrls: ReturnType<typeof animate<number>> | null = null;

    return {
      play() {
        if (ctrls) ctrls.stop();
        ctrls = animate(audioRef.current?.volume || 0, 1, {
          ...audioOnOffTransition,
          onPlay() {
            audioRef.current?.play();
          }
        });
        return ctrls;
      },
      pause() {
        if (ctrls) ctrls.stop();
        ctrls = animate(audioRef.current?.volume || 1, 0, {
          ...audioOnOffTransition,
          onComplete() {
            const audio = audioRef.current;
            if (!audio) return;

            audio.pause();
            if (audio.currentTime > end) audio.currentTime = start;
          }
        });
        return ctrls;
      }
    };
  }, [animate, audioOnOffTransition, end, start]);

  const handleAudioTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // pause and set audio time if it's before start of snippet
    if (start > audio.currentTime) {
      audio.pause();
      audio.currentTime = start;
      audio.volume = 0;
    }

    // update timestamp
    containerRef.current?.querySelectorAll('[data-timestamp]').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      el.innerText = formatTimestamp(audio.currentTime);
    });

    const checkbox = inputRef.current;
    if (!checkbox) return;
    checkbox.disabled = audio.currentTime >= end - 0.01;

    // trigger pause of audio if it's past end - transition time
    if (audio.currentTime >= end - 0.01 && checkbox.checked) {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event('change'));
    }
  }, [start, end]);

  useAnimationFrame(time => {
    if (!inView) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    drawWave(canvas, {
      t: time * -0.01,
      waves: [...fifoWaveQueue.values()],
      rampEase: easing.inOutQuint,
      ramp: 300,
      ...waveOptsRef.current
    });
  });

  useEffect(() => {
    function handleChange(e: Event) {
      const waveTransition = {
        duration: 2,
        ease: easing.outSine
      } satisfies Parameters<typeof animate<number>>[2];

      // checked -> play audio, add wave
      if (e.target instanceof HTMLInputElement && e.target.checked) {
        audioControls.play();

        const wave = { head: 0, tail: 0 };
        fifoWaveQueue.set(wave, wave);
        activeWave.current = wave;

        animate(wave.head, 1, {
          ...waveTransition,
          onUpdate: v => {
            wave.head = v;
          }
        });

        return;
      }

      // unchecked -> pause audio, remove last active wave
      audioControls.pause();

      if (!activeWave.current) return;
      const wave = fifoWaveQueue.get(activeWave.current);
      if (!wave) return;
      animate(wave.tail, 1, {
        ...waveTransition,
        duration: waveTransition.duration * 0.8,
        onUpdate: v => {
          wave.tail = v;
        }
      }).then(() => fifoWaveQueue.delete(wave));
    }

    const input = inputRef.current;
    if (!input) return;

    input.addEventListener('change', handleChange);
    return () => {
      input.removeEventListener('change', handleChange);
    };
  }, [animate, audioControls, fifoWaveQueue]);

  return (
    <Container ref={combineRefs(scope, containerRef)}>
      <Label
        style={{
          '--duration': `${audioOnOffTransition.duration}s`
        }}>
        <input
          ref={inputRef}
          name="fod-audio-clip"
          type="checkbox"
          onChange={e => {
            const target = e.target;
            const checked = target.checked;

            // Stop any playing youtube videos
            document
              .querySelectorAll('iframe[title="Loom video"]')
              .forEach(el => {
                if (!(el instanceof HTMLIFrameElement)) return;
                el.contentWindow?.postMessage(
                  '{"event":"command","func":"stopVideo","args":""}',
                  '*'
                );
              });

            // If we're turning on an audio clip
            // we want to turn off all others.
            if (!checked) return;
            document
              .querySelectorAll(`input[name=${target.getAttribute('name')}]`)
              .forEach(checkbox => {
                if (!(checkbox instanceof HTMLInputElement)) return;
                if (checkbox === target) return;
                checkbox.checked = false;
                checkbox.dispatchEvent(new Event('change'));
              });
          }}
        />
        <span data-timestamp>00:00</span>
        <span aria-hidden="true" className="overlay" data-timestamp>
          00:00
        </span>
      </Label>
      <CanvasContainer>
        <Canvas ref={canvasRef} />
      </CanvasContainer>
      <audio
        ref={audioRef}
        src={src}
        onLoadStart={handleAudioTimeUpdate}
        onTimeUpdate={handleAudioTimeUpdate}
      />
    </Container>
  );
}
